import { InformationCircleIcon } from '@heroicons/react/outline';
import { Tooltip } from '@yoast/ui-library';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { TOOLTIP_MOBILE_MAX_WIDTH } from '../utils/breakpoints';

type InfoTooltipProps = {
  /** Explanatory copy shown in the tooltip. */
  content: string;
  /** Accessible name for the trigger, e.g. the field label. */
  label: string;
};

// Viewport edge padding and the tooltip's own offset from the icon (`ms-3`/`me-3` = 12px).
const VIEWPORT_MARGIN = 8;
const ICON_GAP = 12;
// Upper bound for the bubble's width; matches the library's `max-w-sm`.
const MAX_TOOLTIP_WIDTH = 384;
// Below this the bubble is too narrow to read comfortably, so we prefer whichever
// side is roomier rather than squeezing into the preferred one.
const COMFORTABLE_WIDTH = 160;
// Grace period for moving the pointer off the icon and onto the bubble (which sits
// ICON_GAP away), so the tooltip stays reachable — see WCAG 1.4.13 "Hoverable".
const HIDE_DELAY_MS = 120;

type Placement = {
  position: 'left' | 'right';
  top: number;
  left: number;
  maxWidth: number;
};

/**
 * Info-icon tooltip for form field labels, using the `@yoast/ui-library`
 * `Tooltip` for its look (dark bubble + arrow).
 *
 * Prefer this over `TooltipUncontrolled` when the trigger sits inside a
 * scrolling or `overflow`-hidden container: the library `Tooltip` positions
 * itself absolutely, so it would be clipped there. This renders it through a
 * portal into a fixed-position anchor at the icon, so no ancestor can clip it.
 * (`TooltipUncontrolled` remains for the many inline usages that don't need it.)
 *
 * Two details make the portal work:
 * - The portal content is wrapped in `.yst-root`. The library scopes all of its
 *   component styles (`.yst-tooltip`, its offsets, colours) under `.yst-root`
 *   (added by the app's `<Root>`), so a portal into `document.body` — outside
 *   that root — would otherwise render as unstyled text.
 * - Because the anchor is `fixed`, its coordinates go stale when the page or a
 *   scroll container moves, so placement is recomputed on scroll and resize
 *   while open.
 *
 * Placement opens to the left on desktop (the Brand profile panel is
 * right-aligned, so there is room there) and to the right on mobile (the icon
 * sits near the left edge). Either way the bubble is capped to the space
 * available on the side chosen, and the roomier side wins when the preferred one
 * would be uncomfortably narrow.
 *
 * Accessibility: the trigger is a focusable button, the tooltip is hoverable
 * (see `HIDE_DELAY_MS`), and Escape dismisses it from anywhere on the page.
 */
const InfoTooltip = ({ content, label }: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    cancelHide();
    setOpen(true);
  }, [cancelHide]);

  const hide = useCallback(() => {
    cancelHide();
    setOpen(false);
    setPlacement(null);
  }, [cancelHide]);

  // Leave the bubble on screen briefly so the pointer can travel from the icon
  // onto it without it vanishing (WCAG 1.4.13 "Hoverable").
  const hideAfterDelay = useCallback(() => {
    cancelHide();
    hideTimerRef.current = setTimeout(hide, HIDE_DELAY_MS);
  }, [cancelHide, hide]);

  useEffect(() => cancelHide, [cancelHide]);

  // Compute placement when the tooltip opens, and keep it correct while it stays
  // open. The listeners are only registered while open, so the many closed
  // tooltips on a page don't react to scroll/resize at all. `scroll` is captured
  // so scrolling an ancestor container (not just the page) is caught too.
  useLayoutEffect(() => {
    if (!open) return;

    const computePlacement = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();

      const availLeft = rect.left - ICON_GAP - VIEWPORT_MARGIN;
      const availRight = window.innerWidth - rect.right - ICON_GAP - VIEWPORT_MARGIN;

      // Prefer left on desktop and right on mobile, but fall back to whichever
      // side is roomier when the preferred one is too narrow to read.
      const preferred: 'left' | 'right' =
        window.innerWidth < TOOLTIP_MOBILE_MAX_WIDTH ? 'right' : 'left';
      const availPreferred = preferred === 'left' ? availLeft : availRight;
      const roomier: 'left' | 'right' = availLeft > availRight ? 'left' : 'right';
      const position = availPreferred < COMFORTABLE_WIDTH ? roomier : preferred;

      // Clamp to the space actually available on the chosen side, so the bubble
      // can't spill past the viewport edge even when both sides are tight.
      const avail = position === 'left' ? availLeft : availRight;
      const maxWidth = Math.min(MAX_TOOLTIP_WIDTH, Math.max(0, avail));

      setPlacement({
        position,
        top: rect.top + rect.height / 2,
        // The bubble hangs off the icon's near edge; its own offset class adds the gap.
        left: position === 'left' ? rect.left : rect.right,
        maxWidth,
      });
    };

    computePlacement();
    window.addEventListener('resize', computePlacement);
    window.addEventListener('scroll', computePlacement, true);
    return () => {
      window.removeEventListener('resize', computePlacement);
      window.removeEventListener('scroll', computePlacement, true);
    };
  }, [open]);

  // Escape must dismiss the tooltip however it was opened. A handler on the
  // trigger would only fire while the trigger has focus, missing hover-opened
  // tooltips, so listen at the document level while open.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, hide]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        aria-label={`More information about ${label}`}
        aria-describedby={open ? tooltipId : undefined}
        className="yst-inline-flex yst-shrink-0 yst-rounded-full yst-text-slate-400 hover:yst-text-slate-600 focus:yst-outline-none focus-visible:yst-ring-2 focus-visible:yst-ring-primary-500"
        onMouseEnter={show}
        onMouseLeave={hideAfterDelay}
        onFocus={show}
        onBlur={hide}
      >
        <InformationCircleIcon className="yst-h-4 yst-w-4" />
      </button>
      {open &&
        placement &&
        createPortal(
          <div
            // Sits above the Brand profile panel and its cards; the panel itself
            // creates no stacking context this needs to beat, so a high value is
            // only needed to clear sibling content at the body level.
            className="yst-root yst-fixed yst-z-[9999]"
            style={{ top: placement.top, left: placement.left }}
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
          >
            <Tooltip
              position={placement.position}
              id={tooltipId}
              style={{ maxWidth: placement.maxWidth }}
            >
              {content}
            </Tooltip>
          </div>,
          document.body,
        )}
    </>
  );
};

export default InfoTooltip;
